import { type FormEvent, useEffect, useState } from 'react'
import { useThemeColors } from '@/contexts/ThemeContext'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { theme } from '@/lib/theme'
import type { AuthCredentials, AuthStep } from '@/types/GuestSession'
import TOSModal from '@/components/auth/TOSModal'
import useIsMobile from '@/hooks/useIsMobile'

interface InChatAuthFlowProps {
  step: AuthStep
  onStepComplete: (
    nextStep: AuthStep,
    credentials?: Partial<AuthCredentials>
  ) => void
  onSubmit: (credentials: AuthCredentials) => Promise<void>
  storedEmail?: string
  storedPassword?: string
}

export default function InChatAuthFlow({
  step,
  onStepComplete,
  onSubmit,
  storedEmail = '',
  storedPassword = '',
}: InChatAuthFlowProps) {
  const colors = useThemeColors()
  const isMobile = useIsMobile()
  const [email, setEmail] = useState(storedEmail)
  const [password, setPassword] = useState(storedPassword)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [showTOSModal, setShowTOSModal] = useState(false)

  // Update email and password when props change
  useEffect(() => {
    setEmail(storedEmail)
    setPassword(storedPassword)
  }, [storedEmail, storedPassword])

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setValidationError('')

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setValidationError('Please enter a valid email address')
      return
    }

    // Move to password step
    onStepComplete('password', { email })
  }

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setValidationError('')

    // Validate password length
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters')
      return
    }

    onStepComplete('confirm-password', { email, password })
  }

  const handleConfirmPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setValidationError('')

    // Validate passwords match
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match')
      return
    }

    // Move to TOS step
    onStepComplete('tos', { email, password })
  }

  const handleTosSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setValidationError('')

    // Validate TOS acceptance
    if (!tosAccepted) {
      setValidationError('You must agree to the Terms of Service to create an account')
      return
    }

    // Submit signup
    setIsSubmitting(true)
    try {
      await onSubmit({ email, password })
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : 'Signup failed'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Don't render anything for prompt steps - those are just assistant messages
  if (
    step === 'prompt' ||
    step === 'login-prompt' ||
    step === 'complete'
  ) {
    return null
  }

  // Show banner for check-email step
  if (step === 'check-email') {
    return (
      <div
        style={{
          padding: theme.spacing.md,
          borderTop: `1px solid ${colors.primaryBorder}`,
          backgroundColor: colors.primaryBg,
        }}
      >
        <div
          style={{
            backgroundColor: colors.primaryAccent + '15',
            border: `2px solid ${colors.primaryAccent}`,
            borderRadius: '8px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        >
          <span style={{ fontSize: '24px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📧')}</span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                color: colors.primaryText,
                fontSize: theme.fontSize.md,
                fontWeight: '600',
                marginBottom: '4px',
              }}
            >
              Check your email to confirm signup
            </div>
            <div
              style={{
                color: colors.secondaryText,
                fontSize: theme.fontSize.sm,
              }}
            >
              We've sent a verification link to your inbox
            </div>
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.85; }
          }
        `}</style>
      </div>
    )
  }

  return (
    <>
    <div
      style={{
        padding: `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.xl} ${theme.spacing.md}`,
        borderTop: `1px solid ${colors.primaryBorder}`,
        backgroundColor: colors.primaryBg,
      }}
    >
      {/* Email step */}
      {step === 'email' && (
        <form onSubmit={handleEmailSubmit}>
          <div>
            {/* Static label */}
            <div
              style={{
                fontSize: theme.fontSize.sm,
                color: colors.secondaryText,
                fontFamily: theme.fontFamily.mono,
                marginTop: theme.spacing.xs,
                marginBottom: theme.spacing.xs,
                marginLeft: isMobile ? 0 : '20px',
              }}
            >
              <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📧')}</span> What's your email address?
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}
            >
              {!isMobile && <span style={{ color: colors.secondaryText }}>{'>'}</span>}
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                style={{
                  width: isMobile ? '100%' : '250px',
                  flex: isMobile ? '1 1 100%' : 'none',
                  fontSize: theme.fontSize.md,
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: colors.primaryText,
                  fontFamily: theme.fontFamily.mono,
                  padding: 0,
                  margin: 0,
                  marginBottom: isMobile ? '8px' : 0,
                }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '8px 12px',
                  backgroundColor: colors.visualAccent1,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.15s ease',
                  opacity: isSubmitting ? 0.6 : 1,
                  marginLeft: isMobile ? 0 : 'auto',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                Continue
              </button>
            </div>
          </div>
          {validationError && (
            <div
              style={{
                color: colors.errorBorder,
                fontSize: theme.fontSize.xs,
                marginTop: theme.spacing.xs,
              }}
            >
              {validationError}
            </div>
          )}
        </form>
      )}

      {/* Password step */}
      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit}>
          <div>
            {/* Static label */}
            <div
              style={{
                fontSize: theme.fontSize.sm,
                color: colors.secondaryText,
                fontFamily: theme.fontFamily.mono,
                marginTop: theme.spacing.xs,
                marginBottom: theme.spacing.xs,
                marginLeft: isMobile ? 0 : '20px',
              }}
            >
              <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔐')}</span> Choose a password (min 8 characters)
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}
            >
              {!isMobile && <span style={{ color: colors.secondaryText }}>{'>'}</span>}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                flex: isMobile ? '1 1 100%' : '0 0 auto',
                marginBottom: isMobile ? '8px' : 0,
              }}>
                <input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="••••••••"
                  style={{
                    width: isMobile ? '100%' : '200px',
                    flex: isMobile ? 1 : 'none',
                    fontSize: theme.fontSize.md,
                    backgroundColor: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: colors.primaryText,
                    fontFamily: theme.fontFamily.mono,
                    padding: 0,
                    margin: 0,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    padding: theme.spacing.xs,
                    fontSize: theme.fontSize.md,
                    backgroundColor: 'transparent',
                    color: colors.secondaryText,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: theme.fontFamily.mono,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <span style={{ fontSize: '24px', fontFamily: "'Noto Emoji', sans-serif" }}>{showPassword ? normalizeEmojis('🙈') : normalizeEmojis('👁')}</span>
                </button>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                marginLeft: isMobile ? 0 : 'auto',
                width: isMobile ? '100%' : 'auto',
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setPassword('')
                    setValidationError('')
                    onStepComplete('email', { email })
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s ease',
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: colors.visualAccent1,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s ease',
                    opacity: isSubmitting ? 0.6 : 1,
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
          {validationError && (
            <div
              style={{
                color: colors.errorBorder,
                fontSize: theme.fontSize.xs,
                marginTop: theme.spacing.xs,
              }}
            >
              {validationError}
            </div>
          )}
        </form>
      )}

      {/* Confirm password step (signup only) */}
      {step === 'confirm-password' && (
        <form onSubmit={handleConfirmPasswordSubmit}>
          <div>
            {/* Static label */}
            <div
              style={{
                fontSize: theme.fontSize.sm,
                color: colors.secondaryText,
                fontFamily: theme.fontFamily.mono,
                marginTop: theme.spacing.xs,
                marginBottom: theme.spacing.xs,
                marginLeft: isMobile ? 0 : '20px',
              }}
            >
              <span style={{ fontSize: '24px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('✅')}</span> Confirm your password
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}
            >
              {!isMobile && <span style={{ color: colors.secondaryText }}>{'>'}</span>}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                flex: isMobile ? '1 1 100%' : '0 0 auto',
                marginBottom: isMobile ? '8px' : 0,
              }}>
                <input
                  id="auth-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{
                    width: isMobile ? '100%' : '200px',
                    flex: isMobile ? 1 : 'none',
                    fontSize: theme.fontSize.md,
                    backgroundColor: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: colors.primaryText,
                    fontFamily: theme.fontFamily.mono,
                    padding: 0,
                    margin: 0,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    padding: theme.spacing.xs,
                    fontSize: theme.fontSize.md,
                    backgroundColor: 'transparent',
                    color: colors.secondaryText,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: theme.fontFamily.mono,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <span style={{ fontSize: '24px', fontFamily: "'Noto Emoji', sans-serif" }}>{showConfirmPassword ? normalizeEmojis('🙈') : normalizeEmojis('👁')}</span>
                </button>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                marginLeft: isMobile ? 0 : 'auto',
                width: isMobile ? '100%' : 'auto',
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmPassword('')
                    setValidationError('')
                    onStepComplete('password', { email, password })
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s ease',
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: colors.visualAccent1,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s ease',
                    opacity: isSubmitting ? 0.6 : 1,
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
          {validationError && (
            <div
              style={{
                color: colors.errorBorder,
                fontSize: theme.fontSize.xs,
                marginTop: theme.spacing.xs,
              }}
            >
              {validationError}
            </div>
          )}
        </form>
      )}

      {/* TOS agreement step (signup only) */}
      {step === 'tos' && (
        <form onSubmit={handleTosSubmit}>
          <div>
            {/* Static label */}
            <div
              style={{
                fontSize: theme.fontSize.sm,
                color: colors.secondaryText,
                fontFamily: theme.fontFamily.mono,
                marginTop: theme.spacing.xs,
                marginBottom: theme.spacing.xs,
                marginLeft: isMobile ? 0 : '20px',
              }}
            >
              <span style={{ fontSize: '24px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📜')}</span> Terms of Service Agreement
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}
            >
              {!isMobile && <span style={{ color: colors.secondaryText }}>{'>'}</span>}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: isMobile ? '1 1 100%' : 1,
                marginBottom: isMobile ? '8px' : 0,
              }}>
                <input
                  id="auth-tos-accepted"
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: colors.visualAccent1,
                    flexShrink: 0,
                  }}
                />
                <label
                  htmlFor="auth-tos-accepted"
                  style={{
                    color: colors.primaryText,
                    fontSize: isMobile ? '12px' : theme.fontSize.sm,
                    cursor: 'pointer',
                    fontFamily: theme.fontFamily.mono,
                    whiteSpace: 'nowrap',
                  }}
                >
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      setShowTOSModal(true)
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.primaryAccent,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                      fontSize: isMobile ? '12px' : theme.fontSize.sm,
                    }}
                  >
                    Terms of Service
                  </button>
                </label>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                marginLeft: isMobile ? 0 : 'auto',
                width: isMobile ? '100%' : 'auto',
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setTosAccepted(false)
                    setValidationError('')
                    onStepComplete('confirm-password', { email, password })
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s ease',
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: colors.visualAccent1,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.15s ease',
                    opacity: isSubmitting ? 0.6 : 1,
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  Sign up
                </button>
              </div>
            </div>
            {validationError && (
              <div
                style={{
                  color: colors.errorBorder,
                  fontSize: theme.fontSize.sm,
                  fontFamily: theme.fontFamily.mono,
                  marginTop: theme.spacing.xs,
                }}
              >
                {validationError}
              </div>
            )}
          </div>
        </form>
      )}
    </div>

    {/* Terms of Service Modal */}
    {showTOSModal && <TOSModal onClose={() => setShowTOSModal(false)} />}
    </>
  )
}
